import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LOGO_URL } from "@/lib/content";

const NAV_LINKS = [
  { href: "#services", label: "Services", id: "nav-services" },
  { href: "#work", label: "Work", id: "nav-work" },
  { href: "#manifesto", label: "Approach", id: "nav-approach" },
  { href: "#contact", label: "Contact", id: "nav-contact" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1], delay: 0.1 }}
      className={`fixed top-0 left-0 right-0 z-50 nav-glass ${scrolled ? "" : ""}`}
      data-testid="site-nav"
    >
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 h-[104px] flex items-center justify-between">
        <a
          href="#top"
          onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          className="inline-flex items-center group select-none"
          data-testid="nav-logo"
          aria-label="Trilogy Earthworks — home"
        >
          <img
            src={LOGO_URL}
            alt="Trilogy Earthworks"
            className="h-20 md:h-24 w-auto object-contain block transition-transform duration-500 group-hover:scale-[1.02]"
            draggable={false}
          />
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => scrollTo(l.href)}
              data-testid={l.id}
              className="text-[13px] uppercase tracking-[0.18em] text-[color:var(--te-text)]/80 hover:text-[color:var(--te-primary)] transition-colors font-display font-medium"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => scrollTo("#quote")}
            className="te-btn te-btn-primary hidden sm:inline-flex"
            data-testid="nav-quote-button"
          >
            Request Quote
          </button>
          <button
            onClick={() => setOpen((v) => !v)}
            className="md:hidden te-btn te-btn-ghost !py-2 !px-3 flex flex-col items-center justify-center gap-[5px]"
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
            aria-expanded={open}
          >
            <span className="block w-5 h-[2px] bg-white" />
            <span className="block w-5 h-[2px] bg-white" />
            <span className="block w-5 h-[2px] bg-white" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
            className="md:hidden overflow-hidden border-t border-white/5 bg-[color:var(--te-bg)]/95"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col px-6 py-6 gap-4">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.href}
                  onClick={() => scrollTo(l.href)}
                  className="text-left text-2xl font-display font-bold uppercase tracking-tight"
                  data-testid={`${l.id}-mobile`}
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => scrollTo("#quote")}
                className="te-btn te-btn-primary w-full mt-2"
                data-testid="nav-quote-button-mobile"
              >
                Request Quote
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
